import { Button } from "azure-devops-ui/Button";
import { MenuButton } from "azure-devops-ui/Menu";
import { css, getSafeId, getSafeIdSelector } from "azure-devops-ui/Util";
import React from "react";
import { lazy } from "../../common/components/lazy/lazy";
import { Observer } from "../../common/components/observer/observer";
import { ResponsiveLayout } from "../../common/components/responsivelayout/responsivelayout";
import { Scenario } from "../../common/components/scenario/scenario";
import { useObservable, useSubscription } from "../../common/hooks/useobservable";
import * as Icons from "../components/illustration/icons";
import { SearchForm } from "../components/search/searchform";
import { SessionContext } from "../contexts/session";
import { UserPreferencesContext } from "../contexts/userpreferences";
import { useLayoutStyle } from "../hooks/uselayoutstyle";
import { getActiveSearch } from "../utilities/util";
import { NpmMeControlContainer } from "./mecontrol/mecontrolcontainer";
import { IMeControl } from "./mecontrol/mecontroltypes";

import "./header.css";

declare global {
  interface Window {
    MeControl?: IMeControl;
  }
}

const { MoreButton, SignIn } = window.Resources.Common;
const { AppLauncher, HelpAltText, SettingsAltText, UpgradeAltText } = window.Resources.Header;
const { MobileSearchPlaceholder, SearchPlaceholder } = window.Resources.SearchInput;

const ApplicationPanel = lazy(() => import("../panels/apppanel"));
const HelpPanel = lazy(() => import("../panels/helppanel"));
const SettingsPanel = lazy(() => import("../panels/settingspanel"));

export interface IHeaderProps {
  "aria-hidden"?: boolean;
  className?: string;
}

export function Header(props: IHeaderProps): React.ReactElement {
  const { className } = props;

  const sessionContext = React.useContext(SessionContext);
  const userPreferencesContext = React.useContext(UserPreferencesContext);

  const responsiveElement = React.useRef<HTMLDivElement>(null);

  const [showApplication, setShowApplication] = useObservable(false);
  const [showHelp, setShowHelp] = useObservable(false);
  const [showSettings, setShowSettings] = useObservable(false);

  const layoutStyle = useLayoutStyle();
  const mobileLayout = layoutStyle === "mobile";

  const photoPreferences = useSubscription(userPreferencesContext.getUserPreferences("photo"));

  return (
    <ResponsiveLayout containerElement={responsiveElement} responsiveSelectors={[getSafeIdSelector("header-commands"), getSafeIdSelector("title")]}>
      {({ responsiveIndex }) => (
        <div
          aria-hidden={props["aria-hidden"]}
          className={css("photo-view-header", className, "body-l flex-row flex-noshrink flex-align-center")}
          ref={responsiveElement}
          role="banner"
        >
          <div className="flex-row flex-noshrink padding-right-8">
            {sessionContext.standalone ? (
              <Icons.OneDrive className="onedrive-application-icon margin-8" />
            ) : (
              <Button
                ariaHasPopup={true}
                ariaLabel={AppLauncher}
                className="header-button"
                iconProps={{ className: "padding-horizontal-8", iconName: "WaffleOffice365" }}
                onClick={(event) => {
                  setShowApplication(true);
                  event.preventDefault();
                }}
              />
            )}
          </div>
          <span
            className="photo-view-product-title flex-row flex-noshrink font-weight-semibold padding-right-16 padding-vertical-4"
            id={getSafeId("title")}
          >
            OneDrive
          </span>
          <div className="flex-row flex-grow flex-align-center flex-justify-center">
            {!photoPreferences?.florenceSearch && (
              <SearchForm activeSearch={getActiveSearch(sessionContext)} placeholder={mobileLayout ? MobileSearchPlaceholder : SearchPlaceholder} />
            )}
          </div>
          <Button
            ariaLabel={UpgradeAltText}
            className="header-button"
            href="https://onedrive.live.com/?ocid=PROD%5FOneDrive%2DWeb%5FSuiteNav%5FNormal%5FHeaderDiamond&v=upgrade&hideLeftNav=true"
            iconProps={{ className: "margin-horizontal-8", render: (className: string) => <Icons.Diamond className={className} /> }}
          />
          <div className="flex-row flex-justify-end flex-noshrink" id={getSafeId("header-commands")}>
            <Button
              ariaLabel={SettingsAltText}
              className="header-button"
              iconProps={{ className: "margin-horizontal-8", render: (className: string) => <Icons.Settings className={className} /> }}
              onClick={() => setShowSettings(true)}
            />
            <Button
              ariaLabel={HelpAltText}
              className="header-button"
              iconProps={{ className: "margin-horizontal-8", render: (className: string) => <Icons.Help className={css(className, "no-rotate")} /> }}
              onClick={() => setShowHelp(true)}
            />
          </div>
          <MenuButton
            ariaLabel={MoreButton}
            buttonClassName="header-button"
            className={css("flex-noshrink", responsiveIndex === -1 && "hidden")}
            contextualMenuProps={{
              className: "compact-menu",
              menuProps: {
                id: "overflow-menu",
                items: [
                  {
                    id: "settings",
                    iconProps: { render: (className: string) => <Icons.Settings className={className} /> },
                    onActivate: () => setShowSettings(true),
                    text: SettingsAltText
                  },
                  {
                    id: "help",
                    iconProps: { render: (className: string) => <Icons.Help className={className} /> },
                    onActivate: () => setShowHelp(true),
                    text: HelpAltText
                  }
                ]
              }
            }}
            hideDropdownIcon={true}
            iconProps={{ className: "padding-horizontal-8", iconName: "MoreVertical" }}
            subtle={true}
          />
          <div
            className="photo-header-profile flex-row flex-align-center flex-noshrink padding-horizontal-8 header-mecontrol-tab"
            aria-label={SignIn}
          >
            <NpmMeControlContainer />
          </div>
          <React.Suspense fallback={null}>
            <Observer values={{ showApplication }}>
              {({ showApplication }) =>
                showApplication ? (
                  <Scenario scenarioName="applicationpanel">
                    <ApplicationPanel onDismiss={() => setShowApplication(false)} />
                  </Scenario>
                ) : null
              }
            </Observer>
          </React.Suspense>
          <React.Suspense fallback={null}>
            <Observer values={{ showHelp }}>
              {({ showHelp }) =>
                showHelp ? (
                  <Scenario scenarioName="helppanel">
                    <HelpPanel onDismiss={() => setShowHelp(false)} />
                  </Scenario>
                ) : null
              }
            </Observer>
          </React.Suspense>
          <React.Suspense fallback={null}>
            <Observer values={{ showSettings }}>
              {({ showSettings }) =>
                showSettings ? (
                  <Scenario scenarioName="settingspanel">
                    <SettingsPanel onDismiss={() => setShowSettings(false)} />
                  </Scenario>
                ) : null
              }
            </Observer>
          </React.Suspense>
        </div>
      )}
    </ResponsiveLayout>
  );
}
